import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { calculateOverallPlayerScore, filterPlayersWithSummary } from '../utils/playerValue';
import type { LeagueSettings } from '../utils/playerValue';
import type { Player, PositionalScarcity } from '../types/player';
import type { TradeResult } from '../types/trade';
import { getProcessedSleeperData } from '../utils/sleeperIntegration';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { getPlayerImageWithOverrides, debugPlayerImageMapping } from '../utils/playerImageFix';

// Positional scarcity coefficients based on 12-team PPR leagues
const POSITIONAL_SCARCITY: Record<string, PositionalScarcity> = {
  TE: { position: 'TE', tierBreakpoints: [200, 150, 120, 100, 80], scarcityMultiplier: 1.8, replacementThreshold: 15 },
  QB: { position: 'QB', tierBreakpoints: [300, 250, 220, 200, 180], scarcityMultiplier: 1.2, replacementThreshold: 12 },
  RB: { position: 'RB', tierBreakpoints: [250, 200, 160, 130, 100], scarcityMultiplier: 1.4, replacementThreshold: 24 },
  WR: { position: 'WR', tierBreakpoints: [220, 180, 150, 120, 100], scarcityMultiplier: 1.0, replacementThreshold: 36 },
  K: { position: 'K', tierBreakpoints: [120, 100, 90, 80, 70], scarcityMultiplier: 0.8, replacementThreshold: 12 }
};

interface AlternativeTradeOption {
  players: Player[];
  totalValue: number;
  difference: number;
}

interface BudgetRecommendation {
  player: Player;
  exactMatch: boolean;
}

const TradeCalculator = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<'A' | 'B'>('A');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [teamAPlayers, setTeamAPlayers] = useState<Player[]>([]);
  const [teamBPlayers, setTeamBPlayers] = useState<Player[]>([]);
  const [tradeResult, setTradeResult] = useState<TradeResult | null>(null);
  const [playersPool, setPlayersPool] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [alternativeOptions, setAlternativeOptions] = useState<AlternativeTradeOption[]>([]);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetRecommendations, setBudgetRecommendations] = useState<BudgetRecommendation[]>([]);
  const [showBudgetTool, setShowBudgetTool] = useState(false);

  // League settings for valuation
  const leagueSettings: LeagueSettings = {
    leagueSize: 12,
    startingSlots: { QB: 1, RB: 2, WR: 2, TE: 1, K: 1, DEF: 1 }
  };

  // Player filtering with detailed logging
  // No need for separate validation - using filterPlayersWithSummary now

  const findAlternativeTradeOptions = (targetValue: number, excludePlayerIds: string[]): AlternativeTradeOption[] => {
    const alternatives: AlternativeTradeOption[] = [];
    const availablePlayers = playersPool
      .filter(p => 
        !excludePlayerIds.includes(String(p.id)) && 
        p.value && p.value > 0 &&
        // Only include players with reasonable fantasy value (exclude backup QBs, practice squad players, etc.)
        p.value >= 1.0 &&
        // Ensure player has meaningful projections
        (p.projection || 0) >= 5
      )
      // Sort by value (highest first) to ensure we consider quality players first
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    // Single player alternatives
    const singlePlayerOptions = availablePlayers
      .filter(p => Math.abs((p.value || 0) - targetValue) <= targetValue * 0.15)
      .map(p => ({
        players: [p],
        totalValue: p.value || 0,
        difference: Math.abs((p.value || 0) - targetValue)
      }))
      .sort((a, b) => a.difference - b.difference)
      .slice(0, 3);

    alternatives.push(...singlePlayerOptions);

    // Two player combinations (limited to prevent performance issues)
    // Use top 50 players instead of top 20 to get better combinations, but still limit for performance
    const topPlayers = availablePlayers.slice(0, 50);
    for (let i = 0; i < topPlayers.length; i++) {
      for (let j = i + 1; j < topPlayers.length; j++) {
        const player1 = topPlayers[i];
        const player2 = topPlayers[j];
        const combinedValue = (player1.value || 0) + (player2.value || 0);
        const difference = Math.abs(combinedValue - targetValue);
        
        if (difference <= targetValue * 0.15) {
          alternatives.push({
            players: [player1, player2],
            totalValue: combinedValue,
            difference
          });
        }
      }
    }

    return alternatives
      .sort((a, b) => a.difference - b.difference)
      .slice(0, 6);
  };

  const findBudgetRecommendations = (budget: number): BudgetRecommendation[] => {
    const tolerance = budget * 0.1; // 10% tolerance
    return playersPool
      .filter(p => p.value && p.value > 0)
      .map(p => ({
        player: p,
        exactMatch: Math.abs((p.value || 0) - budget) <= tolerance
      }))
      .sort((a, b) => {
        // Prioritize exact matches, then by value descending
        if (a.exactMatch && !b.exactMatch) return -1;
        if (!a.exactMatch && b.exactMatch) return 1;
        return (b.player.value || 0) - (a.player.value || 0);
      })
      .slice(0, 8);
  };

  const shuffleBudgetRecommendations = () => {
    if (budgetAmount) {
      const budget = parseFloat(budgetAmount);
      const newRecs = findBudgetRecommendations(budget);
      // Shuffle the non-exact matches for variety
      const exactMatches = newRecs.filter(r => r.exactMatch);
      const nonExactMatches = newRecs.filter(r => !r.exactMatch);
      
      // Simple shuffle for non-exact matches
      for (let i = nonExactMatches.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [nonExactMatches[i], nonExactMatches[j]] = [nonExactMatches[j], nonExactMatches[i]];
      }
      
      setBudgetRecommendations([...exactMatches, ...nonExactMatches.slice(0, 8 - exactMatches.length)]);
    }
  };

  const handleBudgetSearch = () => {
    if (budgetAmount) {
      const budget = parseFloat(budgetAmount);
      if (!isNaN(budget) && budget > 0) {
        const recommendations = findBudgetRecommendations(budget);
        setBudgetRecommendations(recommendations);
      }
    }
  };

  useEffect(() => {
    async function fetchPlayersAndImages() {
      setIsLoadingPlayers(true);
      try {
        // Fetch all required Sleeper data
        const { statsMap, projections: projectionsMap, playerStatsMap, teamUsageMap, teamStatsMap, leagueAverages } =
          await getProcessedSleeperData(2024);
        
        const sleeperRes = await fetch('https://api.sleeper.app/v1/players/nfl');
        if (!sleeperRes.ok) throw new Error('Failed to fetch Sleeper data');
        const sleeperData = await sleeperRes.json();
        const playersArray = Object.values(sleeperData);
        console.log(`Fetched ${playersArray.length} players from Sleeper`);
        
        const espnRes = await fetch('https://sports.core.api.espn.com/v3/sports/football/nfl/athletes?limit=18000');
        if (!espnRes.ok) throw new Error('Failed to fetch ESPN data');
        const espnData = await espnRes.json();
        const espnAthletes = espnData.items || [];
        
        const nameToEspnId: Record<string, string> = {};
        espnAthletes.forEach((athlete: any) => {
          if (athlete.fullName && athlete.id) {
            nameToEspnId[athlete.fullName.toLowerCase()] = athlete.id.toString();
          }
        });
        
        const validPlayers = filterPlayersWithSummary(playersArray as any[], 'TradeCalculator', true);
        
        const mapped = validPlayers.map((p: any) => {
          const id = p.player_id;
          const position = p.position as 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';
          const team = p.team || '';
          const stats = statsMap.get(id) || ({} as any);
          const projection = projectionsMap[id] || 0;
          const games = stats.games_played ?? stats.gp ?? 1;
          const previousPPG = (stats.fpts_ppr ?? stats.fpts_half_ppr ?? stats.fpts_std ?? 0) / games;
          const playerStats = playerStatsMap.get(id) || { targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] };
          const playerTeamStats = teamUsageMap.get(team) || { targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] };
          const teamStats = teamStatsMap.get(team) || { playsPerGame: 65, pointsPerGame: 23, redZoneScoreRate: 0.58, offensiveLineRank: 16 };
          const age = p.age || 27;
          const playerName = p.full_name || 'Unknown Player';
          const espnId = playerName ? nameToEspnId[playerName.toLowerCase()] : undefined;
          const imageUrl = getPlayerImageWithOverrides(
            playerName,
            espnId,
            'https://a.espncdn.com/i/headshots/nophoto.png'
          );

          // Debug image mapping for specific players
          if (playerName && ['Lamar Jackson', 'Josh Allen', 'Patrick Mahomes'].includes(playerName)) {
            debugPlayerImageMapping(playerName, espnId);
          }
          return {
            id,
            name: playerName,
            position,
            team,
            rank: 0,
            change: 0,
            imageUrl,
            projection,
            age,
            lastYearPoints: previousPPG * games,
            teamStats,
            leagueAverages,
            playerStats,
            playerTeamStats,
            injuryHistory: [],
            value: 0,
            isActive: p.active !== false,
            lastUpdated: new Date().toISOString()
          } as Player;
        });
        
        console.log(`Processed ${mapped.length} players with enhanced valuations`);
        
        // Calculate replacement levels
        const tempReplacementLevels: Record<string, number> = {};
        Object.entries(POSITIONAL_SCARCITY).forEach(([pos, { replacementThreshold }]) => {
          const posPlayers = mapped.filter(p => p.position === pos)
                                    .sort((a, b) => (b.projection || 0) - (a.projection || 0));
          tempReplacementLevels[pos] = posPlayers[replacementThreshold - 1]?.projection || 0;
        });
        
        // Calculate full ranking scores for all players
        const scored = mapped.map(player => {
          const { overallScore } = calculateOverallPlayerScore(player, leagueSettings, mapped, tempReplacementLevels[player.position] || 0);
          return { ...player, value: overallScore };
        });
        
        setPlayersPool(scored);
      } catch (e) {
        console.error('Error fetching players:', e);
      } finally {
        setIsLoadingPlayers(false);
      }
    }
    
    fetchPlayersAndImages();
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    const lower = query.toLowerCase();
    const results = playersPool
      .filter((p) => p.name && p.name.toLowerCase().includes(lower))
      // Sort by value (highest first) so best players appear first in search
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, 10);
    setSearchResults(results);
    setIsSearching(false);
  };

  const addPlayer = (player: Player, team: 'A' | 'B') => {
    if (team === 'A') {
      setTeamAPlayers(prev => [...prev, player]);
    } else {
      setTeamBPlayers(prev => [...prev, player]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const removePlayer = (playerId: string, team: 'A' | 'B') => {
    if (team === 'A') {
      setTeamAPlayers(prev => prev.filter(p => p.id !== playerId));
    } else {
      setTeamBPlayers(prev => prev.filter(p => p.id !== playerId));
    }
  };

  const analyzeTrade = () => {
    if (teamAPlayers.length === 0 && teamBPlayers.length === 0) {
      setTradeResult(null);
      setAlternativeOptions([]);
      return;
    }
    
    // Use the player values directly from the playersPool (already calculated with full formula)
    const yourTeamValue = teamAPlayers.reduce((sum, player) => sum + (player.value || 0), 0);
    const theirTeamValue = teamBPlayers.reduce((sum, player) => sum + (player.value || 0), 0);
    
    const difference = Math.abs(yourTeamValue - theirTeamValue);
    const avgTradeValue = (yourTeamValue + theirTeamValue) / 2;
    
    // Adjusted threshold for the smaller decimal values from the ranking system
    // Base threshold is 5% of average value, minimum of 0.5, maximum of 2.0
    const baseThreshold = Math.max(0.5, Math.min(2.0, avgTradeValue * 0.05));
    
    // Adjust threshold based on trade size - larger trades can have slightly more variance
    const tradeSizeAdjustment = Math.min(1.5, 1 + (teamAPlayers.length + teamBPlayers.length - 2) * 0.1);
    const threshold = baseThreshold * tradeSizeAdjustment;

    // Find alternative trade options
    const allPlayerIds = [...teamAPlayers, ...teamBPlayers].map(p => String(p.id));
    const alternatives = findAlternativeTradeOptions(Math.max(yourTeamValue, theirTeamValue), allPlayerIds);
    setAlternativeOptions(alternatives);
    
    setTradeResult({
      sideAValue: Math.round(yourTeamValue * 100) / 100,
      sideBValue: Math.round(theirTeamValue * 100) / 100,
      difference: Math.round(difference * 100) / 100,
      threshold: Math.round(threshold * 100) / 100,
      fair: difference <= threshold,
    });
  };

  useEffect(() => {
    analyzeTrade();
  }, [teamAPlayers, teamBPlayers]);

  if (isLoadingPlayers) {
    return (
      <PageLoader 
        pageName="Trade Calculator"
        subtitle="Evaluate your trades to see if you're getting fair value"
        loadingText="Loading player data for trade analysis..."
      />
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4"
      >
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-fiesta-yellow">
            Trade Calculator
          </h1>
          <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Advanced fantasy football trade analysis using our comprehensive ranking formula.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setShowBudgetTool(!showBudgetTool)}
              className="px-4 py-2 bg-fiesta-blue/20 hover:bg-fiesta-blue/30 text-fiesta-blue border border-fiesta-blue/50 rounded-lg transition-colors"
            >
              {showBudgetTool ? 'Hide' : 'Show'} Budget Tool
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10 max-w-2xl mx-auto">
          <div className="flex-1 relative">
            <input
              type="text"
              className="block w-full rounded-lg border border-gray-700 bg-neutral-900 text-gray-100 px-4 py-3 pr-36 text-base shadow-sm focus:ring-fiesta-blue focus:border-fiesta-blue transition"
              placeholder="Add a player (search by name)..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
              autoComplete="off"
            />
            <div className="absolute inset-y-0 right-0 flex items-center">
              <select
                className="h-full py-2 pl-2 pr-8 border-l border-gray-700 bg-neutral-900 text-gray-100 text-base rounded-r-lg focus:outline-none"
                value={selectedTeam}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTeam(e.target.value as 'A' | 'B')}
              >
                <option value="A">Your Team</option>
                <option value="B">Their Team</option>
              </select>
            </div>
            {searchQuery.length > 1 && (
              <div className="absolute z-20 mt-1 left-0 w-full bg-neutral-900 shadow-xl rounded-lg border border-gray-700 max-h-56 overflow-y-auto">
                {isSearching ? (
                  <div className="py-4 text-center">
                    <LoadingSpinner text="Searching players..." size="sm" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-fiesta-blue/10 transition"
                      onClick={() => addPlayer(player, selectedTeam)}
                    >
                      <img
                        src={player.imageUrl}
                        alt={player.name}
                        className="h-7 w-7 rounded-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-100">{player.name}</span>
                        <div className="text-xs text-gray-400">
                          {player.position} • {player.team} • Value: {(player.value || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center text-gray-400">No players found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Budget Tool */}
        {showBudgetTool && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="max-w-2xl mx-auto mb-10 bg-gray-800 border border-gray-600 rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-semibold text-fiesta-yellow mb-4">Budget Player Finder</h3>
            <p className="text-gray-300 text-sm mb-4">
              Enter your budget value to get player recommendations within that range.
            </p>
            <div className="flex gap-3 mb-4">
              <input
                type="number"
                step="0.1"
                min="0"
                className="flex-1 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 px-4 py-2 focus:ring-fiesta-blue focus:border-fiesta-blue"
                placeholder="Enter budget value (e.g., 15.5)"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
              />
              <button
                onClick={handleBudgetSearch}
                className="px-4 py-2 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white rounded-lg transition-colors"
              >
                Search
              </button>
              {budgetRecommendations.length > 0 && (
                <button
                  onClick={shuffleBudgetRecommendations}
                  className="px-4 py-2 bg-fiesta-pink/20 hover:bg-fiesta-pink/30 text-fiesta-pink border border-fiesta-pink/50 rounded-lg transition-colors"
                >
                  Shuffle
                </button>
              )}
            </div>
            
            {budgetRecommendations.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-300">Recommendations:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {budgetRecommendations.map((rec, index) => (
                    <div
                      key={`${rec.player.id}-${index}`}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition ${
                        rec.exactMatch 
                          ? 'bg-green-900/30 border border-green-600/50 hover:bg-green-900/40' 
                          : 'bg-gray-700/50 hover:bg-gray-700/70'
                      }`}
                      onClick={() => addPlayer(rec.player, selectedTeam)}
                    >
                      <img
                        src={rec.player.imageUrl}
                        alt={rec.player.name}
                        className="h-8 w-8 rounded-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-100 truncate">{rec.player.name}</div>
                        <div className="text-xs text-gray-400">
                          {rec.player.position} • {rec.player.team} • Value: {(rec.player.value || 0).toFixed(2)}
                          {rec.exactMatch && <span className="ml-1 text-green-400">✓ Perfect Match</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="rounded-xl bg-gray-800 shadow-lg border border-gray-600 flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-600">
              <span className="text-lg font-semibold text-fiesta-blue">Your Team</span>
              <span className="text-sm text-gray-300">Total Value: <span className="font-bold text-fiesta-yellow">{tradeResult?.sideAValue.toFixed(2) ?? '0.00'}</span></span>
            </div>
            <ul className="flex-1 divide-y divide-gray-700">
              {teamAPlayers.length === 0 && (
                <li className="text-center py-8 text-gray-400 text-sm">No players added yet</li>
              )}
              {teamAPlayers.map((player) => (
                <li key={`A-${player.id}`} className="flex items-center justify-between px-6 py-3 group hover:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <img
                      src={player.imageUrl}
                      alt={player.name}
                      className="h-9 w-9 rounded-full object-cover border border-gray-600"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                    />
                    <div>
                      <div className="font-medium text-white">{player.name}</div>
                      <div className="text-xs text-gray-300">{player.position} • {player.team}</div>
                      <div className="text-xs text-gray-400">
                        Value: {(player.value || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ml-4 text-red-400 opacity-0 group-hover:opacity-100 transition hover:text-red-300"
                    title="Remove"
                    onClick={() => removePlayer(String(player.id), 'A')}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="rounded-xl bg-gray-800 shadow-lg border border-gray-600 flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-600">
              <span className="text-lg font-semibold text-fiesta-pink">Their Team</span>
              <span className="text-sm text-gray-300">Total Value: <span className="font-bold text-fiesta-yellow">{tradeResult?.sideBValue.toFixed(2) ?? '0.00'}</span></span>
            </div>
            <ul className="flex-1 divide-y divide-gray-700">
              {teamBPlayers.length === 0 && (
                <li className="text-center py-8 text-gray-400 text-sm">No players added yet</li>
              )}
              {teamBPlayers.map((player) => (
                <li key={`B-${player.id}`} className="flex items-center justify-between px-6 py-3 group hover:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <img
                      src={player.imageUrl}
                      alt={player.name}
                      className="h-9 w-9 rounded-full object-cover border border-gray-600"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                    />
                    <div>
                      <div className="font-medium text-white">{player.name}</div>
                      <div className="text-xs text-gray-300">{player.position} • {player.team}</div>
                      <div className="text-xs text-gray-400">
                        Value: {(player.value || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ml-4 text-red-400 opacity-0 group-hover:opacity-100 transition hover:text-red-300"
                    title="Remove"
                    onClick={() => removePlayer(String(player.id), 'B')}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {(teamAPlayers.length > 0 || teamBPlayers.length > 0) && (
          <div className="max-w-2xl mx-auto bg-gray-800 border border-gray-600 rounded-xl shadow-lg px-8 py-8 mt-10">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-fiesta-blue mb-2">Trade Analysis</h2>
              {tradeResult && (
                <div className="space-y-2">
                  <div className="flex justify-center items-center gap-8 text-sm">
                    <div>
                      <span className="block text-gray-300">Your Team Value</span>
                      <span className="block text-lg font-bold text-fiesta-blue">{tradeResult.sideAValue.toFixed(2)}</span>
                    </div>
                    <span className="text-gray-400 text-2xl font-extrabold">vs</span>
                    <div>
                      <span className="block text-gray-300">Their Team Value</span>
                      <span className="block text-lg font-bold text-fiesta-pink">{tradeResult.sideBValue.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-lg font-medium">
                    {tradeResult.sideAValue > tradeResult.sideBValue && (
                      <span className="text-fiesta-blue">You are winning by {tradeResult.difference.toFixed(2)}</span>
                    )}
                    {tradeResult.sideBValue > tradeResult.sideAValue && (
                      <span className="text-fiesta-pink">They are winning by {tradeResult.difference.toFixed(2)}</span>
                    )}
                    {tradeResult.sideAValue === tradeResult.sideBValue && (
                      <span className="text-fiesta-yellow">Perfectly even trade</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <span className={`inline-block px-4 py-1 rounded-full font-semibold text-xs
                      ${tradeResult.fair
                        ? 'bg-green-900/40 text-green-300 border border-green-700'
                        : 'bg-red-900/40 text-red-300 border border-red-700'
                      }`}>
                      {tradeResult.fair ? 'Fair Trade' : 'Unbalanced Trade'} 
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    Fairness threshold: <span className="font-bold text-gray-300">{tradeResult.threshold.toFixed(2)}</span>
                    <br />
                    <span className="text-xs text-gray-500">Based on comprehensive ranking formula including age, scarcity, team context, and more</span>
                  </div>
                </div>
              )}
              {!tradeResult && (
                <div className="text-gray-400 text-sm">Add players to see the comprehensive trade analysis</div>
              )}
            </div>
          </div>
        )}

        {/* Alternative Trade Suggestions */}
        {alternativeOptions.length > 0 && (
          <div className="max-w-4xl mx-auto mt-10 bg-gray-800 border border-gray-600 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-fiesta-yellow mb-4">Alternative Trade Options</h2>
            <p className="text-gray-300 text-sm mb-6">
              Here are some alternative trade options with similar values to your current trade:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {alternativeOptions.map((option, index) => (
                <div key={index} className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-medium text-gray-300">Alternative #{index + 1}</span>
                    <span className="text-sm text-fiesta-yellow font-bold">
                      Value: {option.totalValue.toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {option.players.map((player, playerIndex) => (
                      <div key={playerIndex} className="flex items-center gap-2">
                        <img
                          src={player.imageUrl}
                          alt={player.name}
                          className="h-6 w-6 rounded-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                        />
                        <div className="flex-1">
                          <span className="font-medium text-white text-sm">{player.name}</span>
                          <div className="text-xs text-gray-400">
                            {player.position} • {player.team} • {(player.value || 0).toFixed(2)}
                          </div>
                        </div>
                        <button
                          onClick={() => addPlayer(player, selectedTeam)}
                          className="text-xs px-2 py-1 bg-fiesta-blue/20 hover:bg-fiesta-blue/30 text-fiesta-blue rounded transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                  {option.difference < 1 && (
                    <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                      <span>✓</span>
                      <span>Very close match (diff: {option.difference.toFixed(2)})</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TradeCalculator;